import React, { useState, useEffect, useCallback } from 'react';
import { Banknote, Building2, CheckCircle, Clock, Copy, CreditCard, Edit, ExternalLink, Landmark, Link2, Mail, RefreshCw, Send, Settings, Shield, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { BACKEND_URL, MODES_PAIEMENT } from './constants';
import { getToken } from '../../services/tokenService';

export default function PaymentConfigModal({ recu, onClose, onRefreshList }) {
  const [tab, setTab] = useState('infos');
  const [email, setEmail] = useState(recu.email_beneficiaire || '');
  const [editData, setEditData] = useState({
    montant_ttc: recu.montant_ttc || '',
    mode_paiement: recu.mode_paiement || 'virement',
    observations: recu.observations || '',
  });
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sepaForm, setSepaForm] = useState({ nom_debiteur: '', iban: '', reference_mandat: '' });
  const [copied, setCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const headers = { Authorization: `Bearer ${getToken()}` };

  const loadConfig = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/${recu.id}/payment-config`, { headers });
      setConfig(res.data);
      if (res.data.email_beneficiaire) setEmail(res.data.email_beneficiaire);
    } catch { setConfig(null); }
    setLoadingConfig(false);
  }, [recu.id]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSaveInfos = async () => {
    setSaving(true);
    try {
      const payload = { ...editData };
      await axios.put(`${BACKEND_URL}/api/recus-ess/${recu.id}/modifier`, payload, { headers: { ...headers, 'Content-Type': 'application/json' } });
      if (email) {
        await axios.put(`${BACKEND_URL}/api/recus-ess/${recu.id}/email`, { email }, { headers: { ...headers, 'Content-Type': 'application/json' } });
      }
      toast.success('Recu modifie avec succes');
      onRefreshList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur modification'); }
    setSaving(false);
  };

  const handleCreatePaymentLink = async () => {
    if (!email.trim()) { toast.error("L'email est obligatoire pour generer un lien"); return; }
    setGenerating(true);
    try {
      const _res = await axios.post(`${BACKEND_URL}/api/recus-ess/${recu.id}/payment-link`,
        { email, description: `Recu ESS ${recu.numero_recu}` },
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      toast.success('Lien de paiement Stripe cree');
      loadConfig();
      onRefreshList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur creation lien'); }
    setGenerating(false);
  };

  const handleCreateSepa = async (type) => {
    if (!email.trim()) { toast.error("L'email est obligatoire"); return; }
    setGenerating(true);
    try {
      const payload = {
        email,
        type,
        nom_debiteur: sepaForm.nom_debiteur || recu.raison_sociale || '',
      };
      if (type === 'b2b') {
        payload.iban = sepaForm.iban || '';
        payload.reference_mandat = sepaForm.reference_mandat || '';
      }
      const res = await axios.post(`${BACKEND_URL}/api/recus-ess/${recu.id}/sepa-setup`,
        payload,
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      toast.success(`Paiement SEPA ${type.toUpperCase()} configure`);
      if (res.data.checkout_url) {
        window.open(res.data.checkout_url, '_blank');
      }
      loadConfig();
      onRefreshList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur configuration SEPA'); }
    setGenerating(false);
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Lien copie dans le presse-papier');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendEmail = async (type) => {
    if (!email.trim()) { toast.error("L'email est obligatoire"); return; }
    setSendingEmail(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/recus-ess/${recu.id}/send-payment-email`,
        { type, email },
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      if (res.data.simulated) {
        toast.success(`Email simule a ${res.data.email} (Brevo non actif en preview)`);
      } else {
        toast.success(`Email envoye a ${res.data.email}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur envoi email'); }
    setSendingEmail(false);
  };

  const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
  const labelCls = "block text-xs font-medium text-gray-600 mb-1";

  const getStatusBadge = (status) => {
    if (status === 'paid') return <Badge className="bg-emerald-700 text-white text-xs font-semibold"><CheckCircle className="h-3 w-3 mr-1" />Paye</Badge>;
    if (status === 'pending') return <Badge className="bg-amber-600 text-white text-xs font-semibold"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
    return <Badge variant="outline" className="text-xs text-gray-400">{status || 'N/A'}</Badge>;
  };

  const tabs = [
    { id: 'infos', label: 'Informations', icon: Edit },
    { id: 'stripe', label: 'Lien Stripe', icon: CreditCard },
    { id: 'sepa_core', label: 'SEPA CORE', icon: Landmark },
    { id: 'sepa_b2b', label: 'SEPA B2B', icon: Building2 },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={e => e.stopPropagation()} data-testid="payment-config-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-2xl px-6 py-4 text-white flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Settings className="h-5 w-5" /> Configuration paiement</h3>
            <p className="text-amber-100 text-sm">{recu.numero_recu} - {recu.raison_sociale || 'N/A'} - {parseFloat(recu.montant_ttc || 0).toFixed(2)} EUR</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><XCircle className="h-6 w-6" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex-1 min-w-0 px-3 py-3 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors whitespace-nowrap ${tab === t.id ? 'border-b-2 border-orange-500 text-orange-700 bg-orange-50/50' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`payment-tab-${t.id}`}>
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {loadingConfig ? (
            <div className="flex justify-center py-10"><RefreshCw className="h-6 w-6 animate-spin text-orange-400" /></div>
          ) : (
            <>
              {/* ===== TAB: INFOS ===== */}
              {tab === 'infos' && (
                <div className="space-y-4" data-testid="tab-infos">
                  <div>
                    <label className={labelCls}>Email du beneficiaire</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@beneficiaire.com" className={`${inputCls} pl-10`} data-testid="config-email" />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Utilise pour les paiements Stripe et SEPA</p>
                  </div>
                  <div>
                    <label className={labelCls}>Montant Global (EUR)</label>
                    <input type="number" step="0.01" value={editData.montant_ttc} onChange={e => setEditData(p => ({ ...p, montant_ttc: parseFloat(e.target.value) }))} className={inputCls} data-testid="config-montant" />
                  </div>
                  <div>
                    <label className={labelCls}>Mode de paiement</label>
                    <select value={editData.mode_paiement} onChange={e => setEditData(p => ({ ...p, mode_paiement: e.target.value }))} className={inputCls} data-testid="config-mode">
                      {MODES_PAIEMENT.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Observations</label>
                    <textarea rows={2} value={editData.observations} onChange={e => setEditData(p => ({ ...p, observations: e.target.value }))} className={inputCls} data-testid="config-observations" />
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button onClick={handleSaveInfos} disabled={saving} className="bg-amber-500 hover:bg-amber-600" data-testid="config-save-infos">
                      {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />} Enregistrer
                    </Button>
                  </div>
                </div>
              )}

              {/* ===== TAB: STRIPE LINK ===== */}
              {tab === 'stripe' && (
                <div className="space-y-5" data-testid="tab-stripe">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-indigo-800 flex items-center gap-2 mb-1"><CreditCard className="h-4 w-4" /> Lien de paiement Stripe</h4>
                    <p className="text-xs text-indigo-600">Generez un lien de paiement par carte bancaire a envoyer au beneficiaire</p>
                  </div>

                  <div>
                    <label className={labelCls}>Email du beneficiaire *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@beneficiaire.com" className={`${inputCls} pl-10`} data-testid="stripe-email" />
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Montant a payer</span>
                      <span className="text-xl font-bold text-orange-700">{parseFloat(recu.montant_ttc || 0).toFixed(2)} EUR</span>
                    </div>
                  </div>

                  {/* Existing link */}
                  {config?.stripe_payment_link && (
                    <div className={`rounded-xl p-4 border-2 ${config.stripe_payment_link.status === 'paid' || config.stripe_payment_link.live_status === 'paid' ? 'bg-emerald-50 border-emerald-300' : 'bg-blue-50 border-blue-300'}`} data-testid="stripe-link-existing">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Lien actif</span>
                        {getStatusBadge(config.stripe_payment_link.live_status || config.stripe_payment_link.status)}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <input readOnly value={config.stripe_payment_link.url || ''} className="flex-1 px-3 py-2 bg-white border rounded-lg text-xs text-gray-600 truncate" />
                        <Button size="sm" variant="outline" onClick={() => handleCopyLink(config.stripe_payment_link.url)} className="shrink-0" data-testid="stripe-copy-link">
                          {copied ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(config.stripe_payment_link.url, '_blank')} className="shrink-0" data-testid="stripe-open-link">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">Envoye a : {config.stripe_payment_link.email} - Cree le {new Date(config.stripe_payment_link.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                  )}

                  <Button onClick={handleCreatePaymentLink} disabled={generating} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="stripe-generate-link">
                    {generating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                    {config?.stripe_payment_link ? 'Regenerer le lien de paiement' : 'Generer le lien de paiement'}
                  </Button>

                  {config?.stripe_payment_link?.url && (
                    <Button onClick={() => handleSendEmail('stripe')} disabled={sendingEmail} variant="outline" className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="stripe-send-email">
                      {sendingEmail ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Envoyer le lien par email
                    </Button>
                  )}
                </div>
              )}

              {/* ===== TAB: SEPA CORE ===== */}
              {tab === 'sepa_core' && (
                <div className="space-y-5" data-testid="tab-sepa-core">
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-teal-800 flex items-center gap-2 mb-1"><Landmark className="h-4 w-4" /> Prelevement SEPA CORE</h4>
                    <p className="text-xs text-teal-600">Configurez un prelevement SEPA Direct Debit pour les particuliers et entreprises</p>
                  </div>

                  <div>
                    <label className={labelCls}>Email du debiteur *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@debiteur.com" className={`${inputCls} pl-10`} data-testid="sepa-core-email" />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Nom du debiteur</label>
                    <input type="text" value={sepaForm.nom_debiteur} onChange={e => setSepaForm(p => ({ ...p, nom_debiteur: e.target.value }))} placeholder={recu.raison_sociale || 'Nom complet'} className={inputCls} data-testid="sepa-core-nom" />
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Montant du prelevement</span>
                      <span className="text-xl font-bold text-orange-700">{parseFloat(recu.montant_ttc || 0).toFixed(2)} EUR</span>
                    </div>
                  </div>

                  {/* Existing SEPA CORE */}
                  {config?.sepa_core && (
                    <div className={`rounded-xl p-4 border-2 ${config.sepa_core.status === 'paid' || config.sepa_core.live_status === 'paid' ? 'bg-emerald-50 border-emerald-300' : 'bg-teal-50 border-teal-300'}`} data-testid="sepa-core-existing">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Mandat SEPA CORE actif</span>
                        {getStatusBadge(config.sepa_core.live_status || config.sepa_core.status)}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
                        <div><span className="text-gray-400">Email:</span> {config.sepa_core.email}</div>
                        <div><span className="text-gray-400">Debiteur:</span> {config.sepa_core.nom_debiteur}</div>
                        <div><span className="text-gray-400">Montant:</span> {config.sepa_core.montant} EUR</div>
                        <div><span className="text-gray-400">Cree le:</span> {new Date(config.sepa_core.created_at).toLocaleDateString('fr-FR')}</div>
                      </div>
                      {config.sepa_core.url && (
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => handleCopyLink(config.sepa_core.url)} data-testid="sepa-core-copy">
                            <Copy className="h-3.5 w-3.5 mr-1" /> Copier lien
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => window.open(config.sepa_core.url, '_blank')} data-testid="sepa-core-open">
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ouvrir
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  <Button onClick={() => handleCreateSepa('core')} disabled={generating} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="sepa-core-create">
                    {generating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Landmark className="h-4 w-4 mr-2" />}
                    {config?.sepa_core ? 'Reconfigurer SEPA CORE' : 'Configurer le prelevement SEPA CORE'}
                  </Button>

                  {config?.sepa_core?.url && (
                    <Button onClick={() => handleSendEmail('sepa_core')} disabled={sendingEmail} variant="outline" className="w-full border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="sepa-core-send-email">
                      {sendingEmail ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Envoyer le lien par email
                    </Button>
                  )}
                </div>
              )}

              {/* ===== TAB: SEPA B2B ===== */}
              {tab === 'sepa_b2b' && (
                <div className="space-y-5" data-testid="tab-sepa-b2b">
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-2 mb-1"><Building2 className="h-4 w-4" /> Prelevement SEPA B2B</h4>
                    <p className="text-xs text-purple-600">Prelevement inter-entreprises (Business to Business) avec mandat renforce</p>
                  </div>

                  <div>
                    <label className={labelCls}>Email du debiteur *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@entreprise.com" className={`${inputCls} pl-10`} data-testid="sepa-b2b-email" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Nom / Raison sociale du debiteur</label>
                      <input type="text" value={sepaForm.nom_debiteur} onChange={e => setSepaForm(p => ({ ...p, nom_debiteur: e.target.value }))} placeholder={recu.raison_sociale || 'Raison sociale'} className={inputCls} data-testid="sepa-b2b-nom" />
                    </div>
                    <div>
                      <label className={labelCls}>Reference du mandat</label>
                      <input type="text" value={sepaForm.reference_mandat} onChange={e => setSepaForm(p => ({ ...p, reference_mandat: e.target.value }))} placeholder="MNDT-2026-001" className={inputCls} data-testid="sepa-b2b-ref" />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>IBAN du debiteur</label>
                    <div className="relative">
                      <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="text" value={sepaForm.iban} onChange={e => setSepaForm(p => ({ ...p, iban: e.target.value.toUpperCase() }))} placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" className={`${inputCls} pl-10 font-mono text-xs tracking-wider`} data-testid="sepa-b2b-iban" />
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Montant du prelevement</span>
                      <span className="text-xl font-bold text-orange-700">{parseFloat(recu.montant_ttc || 0).toFixed(2)} EUR</span>
                    </div>
                  </div>

                  {/* Existing SEPA B2B */}
                  {config?.sepa_b2b && (
                    <div className={`rounded-xl p-4 border-2 ${config.sepa_b2b.status === 'paid' || config.sepa_b2b.live_status === 'paid' ? 'bg-emerald-50 border-emerald-300' : 'bg-purple-50 border-purple-300'}`} data-testid="sepa-b2b-existing">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Mandat SEPA B2B actif</span>
                        {getStatusBadge(config.sepa_b2b.live_status || config.sepa_b2b.status)}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
                        <div><span className="text-gray-400">Email:</span> {config.sepa_b2b.email}</div>
                        <div><span className="text-gray-400">Debiteur:</span> {config.sepa_b2b.nom_debiteur}</div>
                        <div><span className="text-gray-400">Montant:</span> {config.sepa_b2b.montant} EUR</div>
                        {config.sepa_b2b.iban && <div><span className="text-gray-400">IBAN:</span> {config.sepa_b2b.iban}</div>}
                        {config.sepa_b2b.reference_mandat && <div><span className="text-gray-400">Ref:</span> {config.sepa_b2b.reference_mandat}</div>}
                      </div>
                      {config.sepa_b2b.url && (
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => handleCopyLink(config.sepa_b2b.url)} data-testid="sepa-b2b-copy">
                            <Copy className="h-3.5 w-3.5 mr-1" /> Copier lien
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => window.open(config.sepa_b2b.url, '_blank')} data-testid="sepa-b2b-open">
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ouvrir
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  <Button onClick={() => handleCreateSepa('b2b')} disabled={generating} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="sepa-b2b-create">
                    {generating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
                    {config?.sepa_b2b ? 'Reconfigurer SEPA B2B' : 'Configurer le prelevement SEPA B2B'}
                  </Button>

                  {config?.sepa_b2b?.url && (
                    <Button onClick={() => handleSendEmail('sepa_b2b')} disabled={sendingEmail} variant="outline" className="w-full border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="sepa-b2b-send-email">
                      {sendingEmail ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Envoyer le lien par email
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

